import React, { lazy as lazyComponent, Suspense } from 'react';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import {
  QueryClient,
  QueryClientProvider
} from 'react-query';
import {StoreProvider} from 'easy-peasy';
import './App.css';
import BottomNavigation from './BottomNav';
import { MusicPlayerProvider, useMusicPlayer } from './contexts/MusicPlayerContext';
import CreditWarningDialog from './components/CreditWarningDialog';
import { CreditWarningProvider } from './contexts/CreditWarningContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import { getTheme } from './themes';
import { AppRoutes } from './constants/routes';
import HeaderBar from './HeaderBar';
import {store} from './utils/store'
import { AuthProvider, useAuthProvider } from './utils/auth';
import ContentWrapper from './components/ContentWrapper';
import BetaModeBanner from './components/BetaModeBanner';

import ScrollToTop from './utils/ScrollToTop';

import ErrorBoundary from './components/ErrorBoundary';
import { ListenerOnlyRoute, CreatorOnlyRoute, ListeningRoute, AuthenticatedRoute } from './components/RouteGuards';
import PodcasterOnlyRoute from './components/RouteGuards/PodcasterOnlyRoute';
import PodcastUploadRoute from './components/RouteGuards/PodcastUploadRoute';
import MusicPlayer from './MusicPlayer';
import OfflineSaveProgress from './components/OfflineSaveProgress';
import FlashMessage from './components/FlashMessage';
import {
  Route,
  BrowserRouter as Router,
  Routes,
  useNavigate
} from "react-router-dom";
import AboutYou, { PersonalDetailsGate } from './features/AboutYou';
// Not split: it's what plays music kept for offline listening, so it must
// open with no connection even if it wasn't visited online first
import Downloads from './features/Downloads';
import LegalFooter from './components/LegalFooter';

// Import error boundary from utilities
import { ResizeObserverErrorBoundary } from './utils/errorHandler';

// Future flags are now handled in the Router component below


// Import error handling utilities
import { initializeErrorHandlers } from './utils/errorHandler';

// Each page loads when it's first opened, not all up front: the site was
// one 3.5 MB file that every visitor downloaded on their first visit.
// After a deploy, a page's old file is gone; someone with the site already
// open gets the new version with one reload instead of an error.
const RELOAD_KEY = 'tuneswell:reloaded-for-new-version';
const lazy = (load) => lazyComponent(() => load().then((page) => {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
  } catch (e) {
    // Storage unavailable
  }
  return page;
}).catch((error) => {
  const key = RELOAD_KEY;
  try {
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, '1');
      window.location.reload();
      return new Promise(() => {});
    }
  } catch (e) {
    // Storage unavailable - show the error instead of reloading in a loop
  }
  throw error;
}));
const Credits = lazy(() => import('./features/Credits'));
const AboutTuneswell = lazy(() => import('./features/AboutTuneswell'));
const AboutUs = lazy(() => import('./features/AboutUs'));
const PlatformStatisticsPage = lazy(() => import('./features/PlatformStatisticsPage'));
const AccountCreate = lazy(() => import('./features/AccountCreate'));
const Support = lazy(() => import('./features/Support'));
const DesktopApp = lazy(() => import('./features/DesktopApp'));
const ActEarnings = lazy(() => import('./features/Act/ActEarnings'));
const ActData = lazy(() => import('./features/Act/ActData'));
const ActEdit = lazy(() => import('./features/Creator/ActManagement/ActEdit'));
const ActEditProfile = lazy(() => import('./features/Creator/ActEditProfile'));
const ActFollowersPage = lazy(() => import('./features/Act/ActFollowersPage'));
const ActFollowingPage = lazy(() => import('./features/Act/ActFollowingPage'));
const ActManage = lazy(() => import('./features/Creator/ActManagement/ActManage'));
const ActMusicUpload = lazy(() => import('./features/MusicUpload/ActMusicUpload'));
const ActPage = lazy(() => import('./features/Act/ActPage'));
const ActPlaylistManagement = lazy(() => import('./features/Creator/ActPlaylistManagement'));
const ActProfile = lazy(() => import('./features/Creator/ActProfile'));
const ActSelection = lazy(() => import('./features/MusicUpload/ActSelection'));
const AddActAvatar = lazy(() => import('./features/Act/AddActAvatar'));
const AddActCreators = lazy(() => import('./features/Act/AddActCreators'));
const AddActName = lazy(() => import('./features/Act/AddActName'));
const AddSingle = lazy(() => import('./features/Single/AddSingle'));
const AlbumEarningsDetails = lazy(() => import('./features/Album/AlbumEarningsDetail'));
const AlbumEdit = lazy(() => import('./features/MusicUpload/components/AlbumEdit'));
const AlbumList = lazy(() => import('./features/Album/AlbumList'));
const AlbumManagement = lazy(() => import('./features/MusicManagement/AlbumManagement'));
const AlbumPage = lazy(() => import('./features/Album/AlbumPage'));
const AlbumView = lazy(() => import('./features/Album/AlbumView'));
const PrivateAlbumPage = lazy(() => import('./features/Album/PrivateAlbumPage'));
const PrivateTrackPage = lazy(() => import('./features/Single/PrivateTrackPage'));
const BlockedUsersPage = lazy(() => import('./features/Messaging/BlockedUsersPage'));
const Browsing = lazy(() => import('./features/Browsing'));
const CreateAct = lazy(() => import('./features/Creator/ActManagement/CreateAct'));
const CreatorAccountSettings = lazy(() => import('./features/Creator/CreatorAccountSettings'));
const CreatorActMgmt = lazy(() => import('./features/Creator/CreatorActMgmt'));
const CreatorDash = lazy(() => import('./features/Creator/CreatorDash'));
const CreatorListeningPortal = lazy(() => import('./features/Creator/CreatorListeningPortal'));
const ActListeningPortal = lazy(() => import('./features/Act/ActListeningPortal'));
const DraftsPage = lazy(() => import('./features/Creator/DraftsPage'));
const PodcasterDash = lazy(() => import('./features/Podcaster/PodcasterDash'));
const PodcasterProfile = lazy(() => import('./features/Podcaster/PodcasterProfile'));
const PodcasterAccountSettings = lazy(() => import('./features/Podcaster/PodcasterAccountSettings'));
const PodcasterEarnings = lazy(() => import('./features/Podcaster/PodcasterEarnings'));
const PodcasterConnections = lazy(() => import('./features/Podcaster/PodcasterConnections'));
const PodcasterDrafts = lazy(() => import('./features/Podcaster/PodcasterDrafts'));
const PodcasterListeningPortal = lazy(() => import('./features/Podcaster/PodcasterListeningPortal'));
const PodcasterMessaging = lazy(() => import('./features/Podcaster/PodcasterMessaging'));
const PodcasterData = lazy(() => import('./features/Podcaster/PodcasterData'));
const PodcastUpload = lazy(() => import('./features/PodcastUpload'));
const CreatorEarnings = lazy(() => import('./features/Creator/CreatorEarnings'));
const CreatorData = lazy(() => import('./features/Creator/CreatorData'));
const CreatorCurationEarnings = lazy(() => import('./features/Creator/CreatorCurationEarnings'));
const CreatorEditProfile = lazy(() => import('./features/Creator/CreatorEditProfile'));
const CreatorMessaging = lazy(() => import('./features/Creator/CreatorMessaging'));
const CreatorConnections = lazy(() => import('./features/Creator/CreatorConnections'));
const CreatorPage = lazy(() => import('./features/Creator/CreatorPage'));
const CreatorPlaylistManagement = lazy(() => import('./features/Creator/CreatorPlaylistManagement'));
const CreatorProfile = lazy(() => import('./features/Creator/CreatorProfile'));
const EarningsDetails = lazy(() => import('./features/EarningsDetails'));
const TransactionDetails = lazy(() => import('./features/Creator/TransactionDetails'));
const EarningsBySource = lazy(() => import('./features/Creator/EarningsBySource'));
const TaxSummary = lazy(() => import('./features/Creator/TaxSummary'));
const FollowersPage = lazy(() => import('./features/Followers/FollowersPage'));
const FollowRequestsPage = lazy(() => import('./features/Followers/FollowRequestsPage'));
const FollowingPage = lazy(() => import('./features/Following/FollowingPage'));
const HistoryList = lazy(() => import('./features/History/HistoryList'));
const HistoryPage = lazy(() => import('./features/History/HistoryPage'));
const QueuePage = lazy(() => import('./features/Queue/QueuePage'));
const Home1 = lazy(() => import('./features/Home/HomePreLogin'));
const Home2 = lazy(() => import('./features/Home/HomePostLogin'));
const Feed = lazy(() => import('./features/Feed'));
const Socials = lazy(() => import('./features/Socials'));
const StreamingRateCalculatorPage = lazy(() => import('./features/StreamingRateCalculator'));
const CurationPayCalculatorPage = lazy(() => import('./features/CurationPayCalculator'));
const RoyaltiesCalculatorPage = lazy(() => import('./features/RoyaltiesCalculator'));
const Exclusives = lazy(() => import('./features/Exclusives'));
const LikesPage = lazy(() => import('./features/LikesPage'));
const Library = lazy(() => import('./features/Library'));
const ListenerAccountSettings = lazy(() => import('./features/Listener/ListenerAccountSettings'));
const EmailPreferences = lazy(() => import('./features/EmailPreferences'));
const ListenerCurationManager = lazy(() => import('./features/Listener/ListenerCurationManager'));
const ListenerCurationSettings = lazy(() => import('./features/Listener/ListenerCurationSettings'));
const ListenerDash = lazy(() => import('./features/Listener/ListenerDash'));
const ListenerEditProfile = lazy(() => import('./features/Listener/ListenerEditProfile'));
const ListenerPayments = lazy(() => import('./features/Listener/ListenerPayments'));
const ListenerProfile = lazy(() => import('./features/Listener/ListenerProfile'));
const LoginPage = lazy(() => import('./features/LoginPage'));
const MapDiscovery = lazy(() => import('./features/MapDiscovery'));
const Messaging = lazy(() => import('./features/Messaging/Messaging'));
const NewReleasesPage = lazy(() => import('./features/NewReleasesPage'));
const ArtistsPage = lazy(() => import('./features/ArtistsPage'));
const PlaylistsPage = lazy(() => import('./features/PlaylistsPage'));
const PodcastsPage = lazy(() => import('./features/PodcastsPage'));
const PodcastersPage = lazy(() => import('./features/PodcastersPage'));
const PublicPodcastPage = lazy(() => import('./features/Podcast/PublicPodcastPage'));
const CuratorsPage = lazy(() => import('./features/CuratorsPage'));
const CreatorsPage = lazy(() => import('./features/CreatorsPage'));
const AlbumsPage = lazy(() => import('./features/AlbumsPage'));
const SinglesPage = lazy(() => import('./features/SinglesPage'));
const TopSongsPage = lazy(() => import('./features/TopSongsPage'));
const AlbumRecommendationsPage = lazy(() => import('./features/AlbumRecommendationsPage'));
const RecommendedActsPage = lazy(() => import('./features/RecommendedActsPage'));
const RecommendedCreatorsPage = lazy(() => import('./features/RecommendedCreatorsPage'));
const RecommendedSongsPage = lazy(() => import('./features/RecommendedSongsPage'));
const GenreBrowsePage = lazy(() => import('./features/GenreBrowsePage'));
const NowPlaying = lazy(() => import('./features/NowPlaying'));
const NuPlayer = lazy(() => import('./features/AudioPlayer'));
const PlaylistBuilder = lazy(() => import('./features/PlaylistManagement/PlaylistBuilder'));
const PlaylistDetail = lazy(() => import('./features/PlaylistManagement/PlaylistDetail'));
const PlaylistManager = lazy(() => import('./features/PlaylistManagement/PlaylistManager'));
const PublicActProfile = lazy(() => import('./features/Act/ActProfile'));
const RegisterPage = lazy(() => import('./features/Register'));
const SearchResults = lazy(() => import('./pages/SearchResults'));
const SignupCreatorForm = lazy(() => import('./features/SignupFlow/SignupCreatorForm'));
const SignupPodcasterForm = lazy(() => import('./features/SignupFlow/SignupPodcasterForm'));
const SignupLabelForm = lazy(() => import('./features/SignupFlow/SignupLabelForm'));
const SignupPublisherForm = lazy(() => import('./features/SignupFlow/SignupPublisherForm'));
const SignupInitial = lazy(() => import('./features/SignupFlow/SignupInitial'));
const LabelDash = lazy(() => import('./features/Label/LabelDash'));
const LabelProfile = lazy(() => import('./features/Label/LabelProfile'));
const LabelEditProfile = lazy(() => import('./features/Label/LabelEditProfile'));
const LabelConnections = lazy(() => import('./features/Label/LabelConnections'));
const PublisherDash = lazy(() => import('./features/Publisher/PublisherDash'));
const PublisherProfile = lazy(() => import('./features/Publisher/PublisherProfile'));
const PublisherEditProfile = lazy(() => import('./features/Publisher/PublisherEditProfile'));
const PublisherConnections = lazy(() => import('./features/Publisher/PublisherConnections'));
const PublisherActsPage = lazy(() => import('./features/Publisher/PublisherActsPage'));
const LabelEarnings = lazy(() => import('./features/Label/LabelEarnings'));
const LabelDrafts = lazy(() => import('./features/Label/LabelDrafts'));
const LabelCurationEarnings = lazy(() => import('./features/Label/LabelCurationEarnings'));
const PublisherEarnings = lazy(() => import('./features/Publisher/PublisherEarnings'));
const LabelListeningPortal = lazy(() => import('./features/Label/LabelListeningPortal'));
const LabelAccountSettings = lazy(() => import('./features/Label/LabelAccountSettings'));
const PublisherListeningPortal = lazy(() => import('./features/Publisher/PublisherListeningPortal'));
const PublisherAccountSettings = lazy(() => import('./features/Publisher/PublisherAccountSettings'));
const PublisherUserManagement = lazy(() => import('./features/Publisher/PublisherUserManagement'));
const LabelInvitationAcceptance = lazy(() => import('./features/Label/LabelInvitationAcceptance'));
const PublisherInvitationAcceptance = lazy(() => import('./features/Publisher/PublisherInvitationAcceptance'));
const StartOrganization = lazy(() => import('./features/Organization/StartOrganization'));
const StartArtistProfile = lazy(() => import('./features/Organization/StartArtistProfile'));
const PayoutSetupReturn = lazy(() => import('./features/PayoutSetupReturn'));
const SignupListenerAddCredit = lazy(() => import('./features/SignupFlow/components/SignupListenerAddCredit'));
const SignupListenerForm = lazy(() => import('./features/SignupFlow/SignupListenerForm'));
const SignupListenerPayment = lazy(() => import('./features/SignupFlow/components/SignupListenerPayment'));
const SinglePage = lazy(() => import('./features/Single/SinglePage'));
const ReviewsPage = lazy(() => import('./features/MusicReviews/ReviewsPage'));
const AllReviewsPage = lazy(() => import('./features/MusicReviews/AllReviewsPage'));
const SongUploadForm = lazy(() => import('./features/MusicUpload/SongUploadForm'));
const TermsOfService = lazy(() => import('./features/TermsOfService'));
const PrivacyPolicy = lazy(() => import('./features/PrivacyPolicy'));
const DmcaPolicy = lazy(() => import('./features/DmcaPolicy'));
const TrackManagement = lazy(() => import('./features/MusicManagement/TrackManagement'));
const UploadTypeSelection = lazy(() => import('./features/MusicUpload/UploadTypeSelection'));


// Initialize comprehensive error handlers for ResizeObserver
initializeErrorHandlers();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes - data stays fresh
      cacheTime: 10 * 60 * 1000, // 10 minutes - cache duration
      refetchOnWindowFocus: false, // Don't refetch on window focus
      refetchOnMount: false, // Don't refetch on component mount
      retry: 1, // Only retry once on failure
      retryDelay: 1000, // Wait 1 second before retry
    },
  },
})

// Rendered unconditionally (unlike <MusicPlayer/>, which is hidden for
// anonymous visitors and several authenticated-but-not-listening user types)
// so showCreditWarning() always has a mounted dialog to actually show.
function GlobalCreditWarningDialog() {
  const navigate = useNavigate();
  const { creditWarningDialog, closeCreditWarning } = useMusicPlayer();

  const handleAddCredit = () => {
    closeCreditWarning();
    navigate(AppRoutes.ListenerPayments);
  };

  return (
    <CreditWarningDialog
      open={creditWarningDialog.open}
      onClose={closeCreditWarning}
      trackName={creditWarningDialog.trackName}
      requiredAmount={creditWarningDialog.requiredAmount}
      onAddCredit={handleAddCredit}
    />
  );
}

// Listening and the professional dashboards are separate: whatever was
// playing stops when someone leaves listening mode (or signs out)
function useStopPlaybackOnLeavingListening(isListening) {
  const { stopPlayback } = useMusicPlayer();
  const wasListening = React.useRef(isListening);
  React.useEffect(() => {
    if (wasListening.current && !isListening) stopPlayback();
    wasListening.current = isListening;
  }, [isListening, stopPlayback]);
}

// Behaviour for the installed desktop app (and the website on a computer)
function DesktopAppBehaviour() {
  const navigate = useNavigate();
  const { currentTrack, isPlaying, pause, resumeFromCurrentPosition, next, previous } = useMusicPlayer();

  // A Tuneswell link opened from elsewhere goes to the window that's already
  // open (manifest launch_handler: focus-existing), without a reload, so
  // whatever is playing keeps playing
  React.useEffect(() => {
    if (!('launchQueue' in window)) return;
    window.launchQueue.setConsumer((launchParams) => {
      if (!launchParams.targetURL) return;
      const url = new URL(launchParams.targetURL);
      if (url.origin === window.location.origin) navigate(url.pathname + url.search + url.hash);
    });
  }, [navigate]);

  // The space bar plays and pauses, unless it's typing or pressing a control
  React.useEffect(() => {
    const onKeyDown = (event) => {
      if (event.code !== 'Space' || event.repeat || event.ctrlKey || event.metaKey || event.altKey) return;
      const target = event.target;
      if (target?.closest?.('input, textarea, select, button, a, [contenteditable="true"], [role="slider"], [role="button"], [role="menuitem"], [role="dialog"]')) return;
      if (!currentTrack) return;
      event.preventDefault();
      if (isPlaying) pause();
      else resumeFromCurrentPosition();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [currentTrack, isPlaying, pause, resumeFromCurrentPosition]);

  // The desktop app's Playback menu (desktop/src/main.js)
  React.useEffect(() => {
    const desktop = window.tuneswellDesktop;
    if (!desktop?.onCommand) return undefined;
    return desktop.onCommand((command) => {
      if (command === 'next') next();
      else if (command === 'previous') previous();
      else if (command === 'play-pause' && currentTrack) {
        if (isPlaying) pause();
        else resumeFromCurrentPosition();
      }
    });
  }, [currentTrack, isPlaying, pause, resumeFromCurrentPosition, next, previous]);

  return null;
}

// Shown while a page's code loads, the first time it's opened
function PageLoading() {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }} role="status" aria-label="Loading">
      <CircularProgress />
    </Box>
  );
}

function AppContent() {
  const { isLoading, userType, isListening } = useAuthProvider();
  useStopPlaybackOnLeavingListening(isListening);
  const { theme } = useTheme();
  const muiTheme = getTheme(theme);

  if (isLoading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100vh',
        color: 'text.primary',
        bgcolor: 'background.default'
      }}>
        Loading...
      </Box>
    );
  }

  // Determine if user should have access to streaming interface
  // For creators: always render MusicPlayer (it will conditionally show based on isOwnTrack or listening mode)
  // For others: only show in listening mode
  // Creators can always play (e.g. previewing their own tracks from the
  // dashboard); everyone else while in the listening experience
  const hasStreamingAccess = userType === 'creator' || isListening;

  return (
    <MuiThemeProvider theme={muiTheme}>
      <StoreProvider store={store}>
        <CssBaseline />
        <Box 
          className="App"
          sx={{
            bgcolor: 'background.default',
            color: 'text.primary',
            minHeight: '100vh',
            textAlign: 'left',
            paddingBottom: '80px', // Space for fixed music player
          }}
        >
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <PersonalDetailsGate />
            <DesktopAppBehaviour />
            <ScrollToTop />
            <HeaderBar />
            <BetaModeBanner />
            <ContentWrapper>
              <Box
                component="header"
                className="App-header"
                sx={{
                  minHeight: '100vh',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  fontSize: 'calc(10px + 2vmin)',
                }}
              >
                <p /><p />
                <Suspense fallback={<PageLoading />}>
                <Routes>
                    <Route exact path={AppRoutes.NowPlaying} element={
                      <ListenerOnlyRoute>
                        <NowPlaying />
                      </ListenerOnlyRoute>
                    } />
                    <Route exact path={AppRoutes.Credits} element={
                      <ListenerOnlyRoute>
                        <Credits />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.TrackCredits} element={
                      <ListenerOnlyRoute>
                        <Credits />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.AlbumCredits} element={
                      <ListenerOnlyRoute>
                        <Credits />
                      </ListenerOnlyRoute>
                    } />
                    <Route exact path={AppRoutes.Browsing} element={<Browsing />} />
                    <Route exact path={AppRoutes.MapDiscovery} element={<MapDiscovery />} />
                    <Route exact path={AppRoutes.Downloads} element={
                      <ListenerOnlyRoute>
                        <Downloads />
                      </ListenerOnlyRoute>
                    } />
                    <Route exact path={AppRoutes.AddActName} element={<AddActName />} />
                    <Route exact path={AppRoutes.AddActCreators} element={<AddActCreators />} />
                    <Route exact path={AppRoutes.AddActAvatar} element={<AddActAvatar />} />
                    <Route path={AppRoutes.ActEarnings} element={<ActEarnings />} />
                    <Route path={AppRoutes.ActData} element={
                      <CreatorOnlyRoute>
                        <ActData />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ActTransactionDetails} element={
                      <CreatorOnlyRoute>
                        <ActEarnings />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ActEditProfile} element={<ActEditProfile />} />
                    <Route path={AppRoutes.AlbumList} element={<AlbumList />} />
                    <Route path={AppRoutes.AlbumPage} element={<AlbumPage />} />
                    <Route path="/private/album/:id/:token" element={<PrivateAlbumPage />} />
                    <Route path="/private/track/:id/:token" element={<PrivateTrackPage />} />
                    <Route path={AppRoutes.SinglePage} element={<SinglePage />} />
                    <Route path={AppRoutes.ReviewsPage} element={<ReviewsPage />} />
                    <Route path={AppRoutes.AllReviewsPage} element={<AllReviewsPage />} />
                    <Route path={AppRoutes.ActPage} element={<ActPage />} />
                    <Route path={AppRoutes.ActFollowersPage} element={<ActFollowersPage />} />
                    <Route path={AppRoutes.ActFollowingPage} element={<ActFollowingPage />} />
                    <Route path={AppRoutes.LoginPage} element={<LoginPage />} />
                    <Route path={AppRoutes.RegisterPage} element={<RegisterPage />} />
                    <Route path={AppRoutes.AccountCreate} element={<AccountCreate />} />
                    <Route path={AppRoutes.SignupInitial} element={<SignupInitial />} />
                    {/* <Route path={AppRoutes.SignupName} element={<SignupName />} /> */}
                    {/* <Route path={AppRoutes.SignupDOB} element={<SignupDOB />} /> */}
                    {/* <Route path={AppRoutes.SignupAvatar} element={<SignupAvatar />} /> */}
                    <Route path={AppRoutes.SongUploadForm} element={<SongUploadForm />} />
                    <Route path={AppRoutes.AboutTuneswell} element={<AboutTuneswell />} />
                    <Route path={AppRoutes.AboutUs} element={<AboutUs />} />
                    <Route path={AppRoutes.PlatformStatistics} element={<PlatformStatisticsPage />} />
                    <Route path={AppRoutes.TermsOfService} element={<TermsOfService />} />
                    <Route path={AppRoutes.PrivacyPolicy} element={<PrivacyPolicy />} />
                    <Route path={AppRoutes.DmcaPolicy} element={<DmcaPolicy />} />
                    <Route path={AppRoutes.Support} element={<Support />} />
                    <Route path={AppRoutes.DesktopApp} element={<DesktopApp />} />
                    <Route path={AppRoutes.LikesPage} element={
                      <ListeningRoute>
                        <LikesPage />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Library} element={
                      <ListeningRoute>
                        <Library />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.AudioPlayer} element={
                      <ListeningRoute>
                        <NuPlayer />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.CreatorDash} element={
                      <CreatorOnlyRoute>
                        <CreatorDash />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorListeningPortal} element={
                      <CreatorOnlyRoute>
                        <CreatorListeningPortal />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ActListeningPortal} element={
                      <CreatorOnlyRoute>
                        <ActListeningPortal />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorDrafts} element={
                      <CreatorOnlyRoute>
                        <DraftsPage />
                      </CreatorOnlyRoute>
                    } />                    <Route path={AppRoutes.CreatorPage} element={
                      <CreatorOnlyRoute>
                        <CreatorPage />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorEarnings} element={
                      <CreatorOnlyRoute>
                        <CreatorEarnings />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorData} element={
                      <CreatorOnlyRoute>
                        <CreatorData />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorCurationEarnings} element={
                      <CreatorOnlyRoute>
                        <CreatorCurationEarnings />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.EarningsDetails} element={
                      <CreatorOnlyRoute>
                        <EarningsDetails />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.TransactionDetails} element={
                      <CreatorOnlyRoute>
                        <TransactionDetails />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.EarningsBySource} element={
                      <CreatorOnlyRoute>
                        <EarningsBySource />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.TaxSummary} element={
                      <CreatorOnlyRoute>
                        <TaxSummary />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.AlbumEarningsDetails} element={
                      <CreatorOnlyRoute>
                        <AlbumEarningsDetails />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorEditProfile} element={
                      <CreatorOnlyRoute>
                        <CreatorEditProfile />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorAccountSettings} element={
                      <CreatorOnlyRoute>
                        <CreatorAccountSettings />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorProfile} element={
                      <CreatorOnlyRoute>
                        <CreatorProfile />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.PublicCreatorProfile} element={<CreatorProfile />} />
                                            <Route path={AppRoutes.NewReleasesPage} element={<NewReleasesPage />} />
                                            <Route path={AppRoutes.ArtistsPage} element={<ArtistsPage />} />
                                            <Route path={AppRoutes.PlaylistsPage} element={<PlaylistsPage />} />
                                            <Route path={AppRoutes.PodcastsPage} element={<PodcastsPage />} />
                                            <Route path={AppRoutes.PodcastersPage} element={<PodcastersPage />} />
                                            <Route path={AppRoutes.PublicPodcastPage} element={<PublicPodcastPage />} />
                                            <Route path={AppRoutes.CuratorsPage} element={<CuratorsPage />} />
                                            <Route path={AppRoutes.CreatorsPage} element={<CreatorsPage />} />
                                            <Route path={AppRoutes.AlbumsPage} element={<AlbumsPage />} />
                                            <Route path={AppRoutes.SinglesPage} element={<SinglesPage />} />
                                            <Route path={AppRoutes.TopSongsPage} element={<TopSongsPage />} />
                                            <Route path={AppRoutes.AlbumRecommendationsPage} element={<AlbumRecommendationsPage />} />
                                            <Route path={AppRoutes.RecommendedActsPage} element={<RecommendedActsPage />} />
                                            <Route path={AppRoutes.RecommendedCreatorsPage} element={<RecommendedCreatorsPage />} />
                                            <Route path={AppRoutes.RecommendedSongsPage} element={<RecommendedSongsPage />} />
                                            <Route path={AppRoutes.GenreBrowsePage} element={<GenreBrowsePage />} />
                        <Route path={AppRoutes.CreatorActMgmt} element={
                          <CreatorOnlyRoute>
                            <CreatorActMgmt />
                          </CreatorOnlyRoute>
                        } />
                        <Route path="/creator/acts/:id/playlists" element={
                          <CreatorOnlyRoute>
                            <ActPlaylistManagement />
                          </CreatorOnlyRoute>
                        } />
                        <Route path="/creator/playlists" element={
                          <CreatorOnlyRoute>
                            <CreatorPlaylistManagement />
                          </CreatorOnlyRoute>
                        } />
                        <Route path={AppRoutes.ActSelection} element={
                          <CreatorOnlyRoute>
                            <ActSelection />
                          </CreatorOnlyRoute>
                        } />
                        <Route path={AppRoutes.UploadTypeSelection} element={
                          <CreatorOnlyRoute>
                            <UploadTypeSelection />
                          </CreatorOnlyRoute>
                        } />
                        <Route path={AppRoutes.ActMusicUpload} element={
                          <CreatorOnlyRoute>
                            <ActMusicUpload />
                          </CreatorOnlyRoute>
                        } />
                    <Route path="/track/:id/manage" element={
                      <CreatorOnlyRoute>
                        <ErrorBoundary><TrackManagement /></ErrorBoundary>
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/album/:id/manage" element={
                      <CreatorOnlyRoute>
                        <ErrorBoundary><AlbumManagement /></ErrorBoundary>
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.PlaylistBuilder} element={
                      <ListeningRoute>
                        <ErrorBoundary><PlaylistBuilder /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.UserPlaylists} element={
                      <ListeningRoute>
                        <ErrorBoundary><PlaylistManager /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path="/playlist/:playlistId" element={
                      <ListeningRoute>
                        <ErrorBoundary><PlaylistDetail /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.History} element={
                      <ListeningRoute>
                        <ErrorBoundary><HistoryPage /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.HistoryList} element={
                      <ListeningRoute>
                        <ErrorBoundary><HistoryList /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Queue} element={
                      <ListeningRoute>
                        <ErrorBoundary><QueuePage /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Messages} element={
                      <ListeningRoute>
                        <ErrorBoundary><Messaging /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Conversation} element={
                      <ListeningRoute>
                        <ErrorBoundary><Messaging /></ErrorBoundary>
                      </ListeningRoute>
                    } />
                    <Route path="/messages/blocked" element={
                      <ListeningRoute>
                        <ErrorBoundary><BlockedUsersPage /></ErrorBoundary>
                      </ListeningRoute>
                    } />

<Route path={AppRoutes.CreateAct} element={
                      <CreatorOnlyRoute>
                        <CreateAct />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ActEdit} element={
                      <CreatorOnlyRoute>
                        <ActEdit />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ActProfile} element={<ActProfile />} />
                    <Route path={AppRoutes.PublicActProfile} element={<PublicActProfile />} />
                    <Route path={AppRoutes.ActManage} element={
                      <CreatorOnlyRoute>
                        <ActManage />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/creator/messages" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/creator/messages/:id" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.CreatorConnections} element={
                      <CreatorOnlyRoute>
                        <CreatorConnections />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.AlbumView + '/:albumId'} element={<AlbumView />} />
                    <Route path={AppRoutes.AlbumEdit + '/:albumId'} element={
                      <CreatorOnlyRoute>
                        <AlbumEdit />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.Search} element={<SearchResults />} />
                    <Route path={AppRoutes.StreamingRateCalculator} element={<StreamingRateCalculatorPage />} />
                    <Route path={AppRoutes.CurationPayCalculator} element={<CurationPayCalculatorPage />} />
                    <Route path={AppRoutes.RoyaltiesCalculator} element={<RoyaltiesCalculatorPage />} />
                    <Route path={AppRoutes.Home1} element={<Home1 />} />
                    <Route path={AppRoutes.Home2} element={
                      <ListenerOnlyRoute>
                        <Home2 />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.Feed} element={
                      <ListeningRoute>
                        <Feed />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Socials} element={
                      <ListeningRoute>
                        <Socials />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.Exclusives} element={<Exclusives />} />
                    <Route path={AppRoutes.AddSingle} element={
                      <CreatorOnlyRoute>
                        <AddSingle />
                      </CreatorOnlyRoute>
                    } />
                    <Route path={AppRoutes.ListenerProfile} element={
                      <ListenerOnlyRoute>
                        <ListenerProfile />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.PublicListenerProfile} element={<ListenerProfile />} />
                    <Route path="/followers/:userId" element={<FollowersPage />} />
                    <Route path={AppRoutes.FollowRequests} element={<FollowRequestsPage />} />
                    <Route path="/following/:userId" element={<FollowingPage />} />
                    <Route path={AppRoutes.ListenerPayments} element={
                      <ListenerOnlyRoute>
                        <ListenerPayments />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.ListenerCurationManager} element={
                      <ListenerOnlyRoute>
                        <ListenerCurationManager />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.ListenerCurationSettings} element={
                      <ListenerOnlyRoute>
                        <ListenerCurationSettings />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.ListenerDash} element={
                      <ListeningRoute>
                        <ListenerDash />
                      </ListeningRoute>
                    } />
                    <Route path={AppRoutes.ListenerEditProfile} element={
                      <ListenerOnlyRoute>
                        <ListenerEditProfile />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.ListenerAccountSettings} element={
                      <ListenerOnlyRoute>
                        <ListenerAccountSettings />
                      </ListenerOnlyRoute>
                    } />
                    <Route path={AppRoutes.EmailPreferences} element={<EmailPreferences />} />
                    <Route path={AppRoutes.SignupListenerForm} element={<SignupListenerForm />} />
                    <Route path={AppRoutes.SignupCreatorForm} element={<SignupCreatorForm />} />
                    <Route path={AppRoutes.SignupPodcasterForm} element={<SignupPodcasterForm />} />
                    <Route path={AppRoutes.SignupLabelForm} element={<SignupLabelForm />} />
                    <Route path={AppRoutes.SignupPublisherForm} element={<SignupPublisherForm />} />
                    <Route path={AppRoutes.LabelDash} element={<LabelDash />} />
                    <Route path={AppRoutes.PublisherDash} element={<PublisherDash />} />
                    <Route path={AppRoutes.PodcasterDash} element={
                      <PodcasterOnlyRoute>
                        <PodcasterDash />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterProfile} element={
                      <PodcasterOnlyRoute>
                        <PodcasterProfile />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PublicPodcasterProfile} element={<PodcasterProfile />} />
                    <Route path={AppRoutes.PodcasterAccountSettings} element={
                      <PodcasterOnlyRoute>
                        <PodcasterAccountSettings />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterEarnings} element={
                      <PodcasterOnlyRoute>
                        <PodcasterEarnings />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterData} element={
                      <PodcasterOnlyRoute>
                        <PodcasterData />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterConnections} element={
                      <PodcasterOnlyRoute>
                        <PodcasterConnections />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterDrafts} element={
                      <PodcasterOnlyRoute>
                        <PodcasterDrafts />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcasterListeningPortal} element={
                      <PodcasterOnlyRoute>
                        <PodcasterListeningPortal />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path={AppRoutes.PodcastUpload} element={
                      <PodcastUploadRoute>
                        <PodcastUpload />
                      </PodcastUploadRoute>
                    } />
                    <Route path={AppRoutes.LabelEarnings} element={<LabelEarnings />} />
                    <Route path={AppRoutes.LabelDrafts} element={<LabelDrafts />} />
                    <Route path={AppRoutes.LabelCurationEarnings} element={<LabelCurationEarnings />} />
                    <Route path={AppRoutes.PublisherEarnings} element={<PublisherEarnings />} />
                    <Route path={AppRoutes.PublisherUserManagement} element={<PublisherUserManagement />} />
                    <Route path={AppRoutes.LabelListeningPortal} element={<LabelListeningPortal />} />
                    <Route path={AppRoutes.PublisherListeningPortal} element={<PublisherListeningPortal />} />
                    <Route path={AppRoutes.LabelAccountSettings} element={<LabelAccountSettings />} />
                    <Route path={AppRoutes.LabelInvitationAcceptance} element={<LabelInvitationAcceptance />} />
                    <Route path={AppRoutes.PublisherInvitationAcceptance} element={<PublisherInvitationAcceptance />} />
                    <Route path={AppRoutes.StartLabel} element={<AuthenticatedRoute><StartOrganization kind="label" /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.StartPublisher} element={<AuthenticatedRoute><StartOrganization kind="publisher" /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.StartArtistProfile} element={<AuthenticatedRoute><StartArtistProfile /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.StartPodcast} element={<AuthenticatedRoute><StartArtistProfile kind="podcast" /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.AboutYou} element={<AuthenticatedRoute><AboutYou /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.PayoutSetupReturn} element={<AuthenticatedRoute><PayoutSetupReturn /></AuthenticatedRoute>} />
                    <Route path={AppRoutes.PublisherAccountSettings} element={<PublisherAccountSettings />} />
                    <Route path={AppRoutes.LabelProfile} element={<LabelProfile />} />
                    <Route path={AppRoutes.PublicLabelProfile} element={<LabelProfile />} />
                    <Route path={AppRoutes.LabelEditProfile} element={<LabelEditProfile />} />
                    <Route path={AppRoutes.LabelConnections} element={<LabelConnections />} />
                    <Route path={AppRoutes.PublisherProfile} element={<PublisherProfile />} />
                    <Route path={AppRoutes.PublicPublisherProfile} element={<PublisherProfile />} />
                    <Route path={AppRoutes.PublisherEditProfile} element={<PublisherEditProfile />} />
                    <Route path={AppRoutes.PublisherConnections} element={<PublisherConnections />} />
                    <Route path={AppRoutes.PublisherActs} element={<PublisherActsPage />} />
                    <Route path="/label/messages" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/label/messages/:id" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/publisher/messages" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/publisher/messages/:id" element={
                      <CreatorOnlyRoute>
                        <CreatorMessaging />
                      </CreatorOnlyRoute>
                    } />
                    <Route path="/podcaster/messages" element={
                      <PodcasterOnlyRoute>
                        <PodcasterMessaging />
                      </PodcasterOnlyRoute>
                    } />
                    <Route path="/podcaster/messages/:id" element={
                      <PodcasterOnlyRoute>
                        <PodcasterMessaging />
                      </PodcasterOnlyRoute>
                    } />
                    {/* <Route path={AppRoutes.SignupEmailCreator} element={<SignupEmailCreator />} /> */}
                    {/* <Route path={AppRoutes.SignupEmailListener} element={<SignupEmailListener />} /> */}
                    {/* <Route path={AppRoutes.SignupCreatorName} element={<SignupCreatorName />} /> */}
                    {/* <Route path={AppRoutes.SignupAvatarCreator} element={<SignupAvatarCreator />} /> */}
                    <Route path={AppRoutes.SignupListenerPayment} element={<SignupListenerPayment />} />
                    <Route path={AppRoutes.SignupListenerAddCredit} element={<SignupListenerAddCredit />} />
                    <Route path="/" element={<Home1 />} />
                    <Route path="*" element={<Home1 />} />
                  </Routes>
                </Suspense>
              </Box>
              <LegalFooter />
                </ContentWrapper>

                {/* Only show streaming interface components for non-creators */}
                {hasStreamingAccess && (
                  <>
                    <BottomNavigation />
                    <MusicPlayer />
                  </>
                )}
                {/* Always mounted, regardless of hasStreamingAccess, so a
                    blocked play attempt (e.g. an anonymous visitor) can
                    actually show the login/credit prompt. */}
                <GlobalCreditWarningDialog />
                <OfflineSaveProgress />
                <FlashMessage />
              </Router>
            </Box>
          </StoreProvider>
        </MuiThemeProvider>
  );
}

function App() {
  return (
    <ResizeObserverErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <ThemeProvider>
          <AuthProvider>
            <MusicPlayerProvider>
              <CreditWarningProvider>
                <AppContent />
              </CreditWarningProvider>
            </MusicPlayerProvider>
          </AuthProvider>
        </ThemeProvider>
      </QueryClientProvider>
    </ResizeObserverErrorBoundary>
  );
}

export default App;
